<template>
  <div
    v-if="!!phaseData && !loading && pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW !== ECustomSummmarizedflow.SANOFI"
  >
    <div>
      <span class="font-weight-medium font-size-16 title-text">Altura e peso do paciente</span>
      <div class="d-flex mt-2">
        <v-text-field
          label="Altura (cm)"
          v-model="phaseData.heightCm"
          @focus="savePreviousPhaseData"
          @blur="patchNewPhaseData"
          filled
          class="mr-6"
          type="number"
          clearable
        />
        <v-text-field
          label="Peso (kg)"
          v-model="phaseData.weightKg"
          @focus="savePreviousPhaseData"
          @blur="patchNewPhaseData"
          filled
          type="number"
          clearable
        />
      </div>
    </div>
    <div v-if="currentMedicines.length > 0">
      <field-label
        label="Medicamentos em uso"
        class="text-h6 d-flex flex-column"
        titleColorClass="grey--text text--darken-2"
      >
        <span
          class="mt-2 grey--text text--darken-4 text-body-2"
          v-for="(medicine, index) in currentMedicines"
          :key="`medicine${index}`"
          >{{ medicine }}</span
        >
      </field-label>
    </div>
    <v-sheet v-else>
      <span class="mt-2 black--text text-body-2">Nenhum medicamento em uso.</span>
    </v-sheet>

    <field-label
      label="Novos medicamentos receitados"
      class="text-h6 d-flex flex-column mt-4"
      titleColorClass="grey--text text--darken-2"
    >
      <span class="mt-2 black--text text-body-2" v-if="phaseData.selectedTreatments.length === 0">
        Nenhum medicamento selecionado.
      </span>
      <div
        v-for="(treatment, index) in phaseData.selectedTreatments"
        :key="`treatment-selected${index}`"
        class="d-flex mt-2 elevation-1 pa-2 flex-column"
      >
        <div class="d-flex flex-column">
          <div class="d-flex align-center" :class="{ 'mb-2': $vuetify.breakpoint.smAndDown }">
            <v-btn @click="phaseData.selectedTreatments.splice(index, 1)" icon large color="red" class="mr-4">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
            <span class="grey--text text--darken-4">{{ treatment.medicine.name }}</span>
          </div>
          <div class="d-flex align-center flex-wrap">
            <select-field
              class="mx-4 flex-grow-1"
              dense
              :items="getTreatmentDosagesOptions(treatment.dosages)"
              v-model="treatment.selectedDosage"
              hideDetails
              v-if="getTreatmentDosagesOptions(treatment.dosages).length > 0"
              returnObject
            >
              <template #item="{ item }">
                <div class="d-flex flex-column py-2 gap-2">
                  <span>{{ item.text }}</span>
                  <span><strong>Posologia: </strong>{{ item.value }}</span>
                  <v-divider />
                </div>
              </template>
              <template #selection="{ item }">
                <div class="d-flex flex-column py-2">
                  <span>{{ item.text }}</span>
                  <span class="mt-2"><strong>Posologia: </strong>{{ item.value }}</span>
                </div>
              </template>
            </select-field>
            <span v-else class="mx-auto text-body-1">Não existem prescrições cadastradas</span>

            <div class="d-flex gap-2 ml-auto" :class="{ 'mr-auto mt-2': $vuetify.breakpoint.smAndDown }">
              <v-tooltip top max-width="600px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon large color="black" style="cursor: default" v-bind="attrs" v-on="on" class="mr-2">
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
                <span>{{ treatment.medicine.description }}</span>
              </v-tooltip>

              <v-tooltip top max-width="600px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="!treatment.selectedDosage"
                    @click="saveAndPrintPrescription({ treatment })"
                  >
                    <v-icon>mdi-file-sign</v-icon>
                  </v-btn>
                </template>
                <span>Imprimir receita</span>
              </v-tooltip>

              <v-tooltip top max-width="600px">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    large
                    color="primary"
                    @click="openPrintExamPrescriptionModal(treatment)"
                    v-bind="attrs"
                    v-on="on"
                    :disabled="treatment.prescriptions.length === 0"
                  >
                    <v-icon>mdi-test-tube</v-icon>
                  </v-btn>
                </template>
                <span>Imprimir prescrição de exames</span>
              </v-tooltip>
            </div>
          </div>
        </div>

        <span
          class="text-subtitle-2 grey--text text--darken-1 ml-4 mt-2"
          style="white-space: pre-line"
          v-if="treatment.selectedDosage"
          ><b class="black--text">Posologia:</b>
          <v-btn
            x-small
            v-if="treatment.selectedDosage.value.length > 180"
            text
            color="primary"
            @click="toggleDosage(treatment.id)"
            class="text-caption"
            >{{ dosageDisplay[treatment.id] ? 'Esconder' : 'Mostrar tudo' }}</v-btn
          ><br /><span v-if="dosageDisplay[treatment.id] || treatment.selectedDosage.value.length <= 180">{{
            treatment.selectedDosage.value
          }}</span
          ><span v-else class="text-subtitle-2 grey--text text--darken-1">{{
            treatment.selectedDosage.value.slice(0, 180) + '...'
          }}</span></span
        >
        <span
          class="text-subtitle-2 grey--text text--darken-1 ml-4 mt-2"
          style="white-space: pre-line"
          v-if="treatment.guidelines"
          ><b class="black--text">Orientações:</b>
          <v-btn
            x-small
            v-if="treatment.guidelines.length > 180"
            text
            color="primary"
            @click="toggleGuideline(treatment.id)"
            class="text-caption"
            >{{ guidelineDisplay[treatment.id] ? 'Esconder' : 'Mostrar tudo' }}</v-btn
          ><br /><span v-if="guidelineDisplay[treatment.id] || treatment.guidelines.length <= 180">{{
            treatment.guidelines
          }}</span
          ><span v-else class="text-subtitle-2 grey--text text--darken-1">{{
            treatment.guidelines.slice(0, 180) + '...'
          }}</span></span
        >
      </div>
    </field-label>

    <field-label
      v-for="(prescription, index) in phaseData.customPrescriptions"
      :label="`Receita customizada ${index + 1}`"
      :key="`customPrescription${index + 1}`"
      class="my-4"
    >
      <template #label-extra>
        <v-btn icon color="red" @click="removeCustomPrescription(index)"><v-icon>mdi-delete</v-icon></v-btn>

        <v-btn
          icon
          large
          color="primary"
          :loading="loading"
          @click="saveAndPrintPrescription({ customText: phaseData.customPrescriptions[index] })"
        >
          <v-icon>mdi-printer</v-icon>
        </v-btn>
      </template>
      <jodit-editor v-model="phaseData.customPrescriptions[index]" :buttons="buttons" />
    </field-label>

    <v-row dense class="mt-2">
      <v-btn class="ml-auto" color="primary" small @click="addCustomPrescription"
        ><v-icon class="mr-2">mdi-plus</v-icon>Nova receita customizada</v-btn
      >
    </v-row>
    <field-label label="Medicamentos indicados" class="text-h6 mt-4" titleColorClass="grey--text text--darken-2">
      <template #label-extra>
        <v-switch v-model="showAllTreatments" label="Exibir todos os medicamentos" class="ml-auto" />
      </template>
      <template v-if="error">
        <h3>Um erro ocorreu ao tentar carregar os medicamentos!</h3>
        <v-btn class="flex-grow-0" large color="primary" @click="loadData" :loading="loading"
          >tentar novamente <v-icon class="ml-2" large>mdi-refresh</v-icon></v-btn
        >
      </template>
      <div v-else>
        <span class="mt-2 black--text text-body-2" v-if="treatmentList.length === 0">Nenhum medicamento indicado.</span>
        <p
          v-if="asasNotPositive && attendance.disease.id === diseases.ESPONDILOARTRITIS"
          class="my-2 text-subtitle-1 grey--text text--darken-4"
        >
          Essa inteligência foi criada utilizando critérios classificatórios e guidelines de diagnóstico e tratamento
          focadas em espondiloartrites. Entretanto, alguns casos podem não se enquadrar nesses critérios ou ainda
          apresentar um diagnóstico alternativo. Sugerimos, nesse caso, a discussão do caso com um médico referência no
          tema. Clique no menu à direita em
          <v-icon color="primary">mdi-human-greeting-proximity</v-icon> (INTERCONSULTA) para discutir o seu caso com um
          EXPERT.
        </p>

        <div
          v-for="treatment in treatmentList"
          :key="treatment.id"
          class="d-flex flex-column elevation-1 mt-2 pa-2"
          style="cursor: pointer"
        >
          <div class="d-flex align-center mb-2">
            <v-icon color="primary" class="mr-4" large @click="phaseData.selectedTreatments.push(treatment)"
              >mdi-plus</v-icon
            >
            <span style="flex-grow-1">{{ treatment.medicine.name }}</span>
          </div>

          <span
            class="text-subtitle-2 grey--text text--darken-1 ml-4 mt-2"
            :style="{ whiteSpace: 'pre-line' }"
            v-if="treatment.medicine.description"
          >
            <b class="black--text">Descrição:</b>
            <v-btn
              x-small
              v-if="treatment.medicine.description.length > 180"
              text
              color="primary"
              @click="toggleDescription(treatment.id)"
              class="text-caption"
              >{{ descriptionDisplay[treatment.id] ? 'Esconder' : 'Mostrar tudo' }}</v-btn
            ><br />
            <span
              v-if="descriptionDisplay[treatment.id] || treatment.medicine.description.length <= 180"
              class="text-subtitle-2 grey--text text--darken-1"
            >
              {{ treatment.medicine.description }}
            </span>
            <span v-else class="text-subtitle-2 grey--text text--darken-1">
              {{ treatment.medicine.description.slice(0, 180) + '...' }}
            </span>
          </span>
        </div>
      </div>
    </field-label>
    <v-dialog v-model="examModal" v-if="!!currentTreatment" max-width="600px">
      <v-card class="pb-4">
        <v-card-title class="justify-center"
          >Impressão de exames para {{ currentTreatment.medicine.name }}</v-card-title
        >

        <field-label
          v-if="examPrescriptionTemplates.length > 0"
          label="Modelo de impressão dos exames"
          titleClass="text-body-1"
          titleColorClass="black--text text--lighten-1"
          class="px-8"
        >
          <select-field
            :items="examPrescriptionTemplates"
            v-model="selectedExamPrescriptionTemplateId"
            itemValue="id"
            itemText="name"
            itemid="id"
            :disabled="examPrescriptionTemplates.length === 1"
          />
        </field-label>

        <div
          v-for="exam in currentTreatmentExams"
          :key="exam.id"
          class="d-flex align-center justify-space-between mx-12"
        >
          <span class="text-body-1 black--text">{{ exam.name }}</span>

          <download-frame :url="printExamPrescriptionUrl(exam.id)" remoteUrl>
            <template #default="{ loading }">
              <v-btn icon large color="primary" :loading="loading">
                <v-icon>mdi-printer</v-icon>
              </v-btn>
            </template>
          </download-frame>
        </div>
      </v-card>
    </v-dialog>

    <select-printing-template-dialog
      v-if="dialog.printingTemplate.show"
      v-model="dialog.printingTemplate.show"
      type="receipt"
      v-bind="dialog.printingTemplate.data"
      @closed="closePrintTemplateDialog"
    />
  </div>
  <div
    v-else-if="
      !!phaseData &&
      attendance.disease.id === diseases.ATOPIC_DERMATITIS &&
      pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW === ECustomSummmarizedflow.SANOFI
    "
  >
    <field-label :label="'Qual o tratamento proposto?'" required>
      <v-row>
        <v-col v-for="option in medicineOptions" :key="option.value" lg="4" cols="6">
          <v-checkbox
            class="mt-4"
            :label="option.text"
            v-model="phaseData.medicines.selected"
            :value="option.value"
            hide-details
          />
        </v-col>
        <v-col cols="12">
          <text-field
            label="Qual?"
            v-if="phaseData.medicines.selected.includes('other')"
            value="other"
            :required="phaseData.medicines.selected.includes('other')"
            v-model="phaseData.medicines.other"
          />
        </v-col>
      </v-row>
    </field-label>
  </div>
</template>
<script>
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import DownloadFrame from '@/components/DownloadFrame.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import SelectField from '@/components/SelectField.vue'
import TextField from '@/components/TextField.vue'
import { ECustomSummmarizedflow } from '@/constants/customSummarizedFlow'
import { DISEASES } from '@/constants/disease'
import { MEDICINE_PERIODS } from '@/constants/medicine'
import { TREATMENT_EXHIBITION_RULES } from '@/store/attendance/config/treatmentExhibitionConfig'
import { createNamespacedHelpers } from 'vuex'

const {
  mapGetters: attendanceGettersMap,
  mapActions: attendanceActionsMap,
  mapState: attendanceStateMap,
} = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')
const { mapGetters: patientGettersMap, mapActions: patientActionsMap } = createNamespacedHelpers('patient')
import SelectPrintingTemplateDialog from '@/components/PrintingTemplate/SelectPrintingTemplateDialog.vue'

export default {
  components: { FieldLabel, SelectField, DownloadFrame, TextField, SelectPrintingTemplateDialog },
  extends: BasePhase,
  computed: {
    ...attendanceStateMap(['isServerSyncedData', 'isLocalSyncedData', 'isSavingData']),
    ...attendanceGettersMap(['getPhaseDataByKey', 'attendanceDiseaseId', 'attendance', 'attendancePatient']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    ...patientGettersMap(['patientCurrentWeightKg', 'patientCurrentHeightCm']),
    ECustomSummmarizedflow() {
      return ECustomSummmarizedflow
    },
    stepsPhaseData() {
      return this.getPhaseDataByKey('steps')
    },
    showTemplateSelect() {
      return (
        this.medicinePrescriptionTemplates.length > 0 &&
        (this.phaseData.selectedTreatments.length > 0 || this.phaseData.customPrescriptions?.length > 0)
      )
    },
    medicineHistory() {
      if (this.getPhaseDataByKey('dados_especificos')) {
        return Object.values(this.getPhaseDataByKey('dados_especificos')?.medicines || {}) || []
      } else {
        return Object.values(this.getPhaseDataByKey('historico_medicamentoso')?.medicines || {}) || []
      }
    },
    currentMedicines() {
      const currentMedicinesStringArr = this.medicineHistory
        .filter((m) => m.periods.some((p) => !p.end))
        .map((m) => {
          let period = m.periods.find((p) => !p.end && p.start)
          if (period?.dosage) {
            return `${m.medicine.name} desde ${this.$util.toFormattedDate(period.start)} com dosagem máxima de ${
              period.dosage
            }mg ${MEDICINE_PERIODS[period.period].replace('(n)', period.length)}.`
          } else {
            return period ? `${m.medicine.name} desde ${this.$util.toFormattedDate(period.start)}` : m.medicine.name
          }
        })

      return currentMedicinesStringArr.sort((a, b) => a.localeCompare(b))
    },
    treatmentList() {
      return this.shownTreatments
        .filter((m) => !this.phaseData.selectedTreatments.some((s) => s.id === m.id))
        .sort((a, b) => a.medicine.name.localeCompare(b.medicine.name))
    },
    currentTreatmentExams() {
      return this.currentTreatment?.prescriptions
        ?.filter((p) => !this.currentTreatment.selectedDosage || p[this.currentTreatment.selectedDosage.key])
        .map((p) => ({ ...p, name: p.exams.map((e) => e.name).join(', ') }))
    },
    medicinePrescriptionTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    examPrescriptionTemplates() {
      return this.templates.map((t) => ({ id: t.id, name: t.name }))
    },
    asasNotPositive() {
      return (
        this.attendance.disease.id === DISEASES.ESPONDILOARTRITIS &&
        this.attendance.summary?.asasPeriferico?.value !== '+' &&
        this.attendance.summary?.asasAxial?.value != '+'
      )
    },
    shownTreatments() {
      if (this.showAllTreatments) {
        return this.treatments.filter((treatment) => {
          const flowExclusion = treatment.exhibitionConfig?.flowExclusion || []
          if (!flowExclusion.includes('exclude_from_all_list_sanofi_flow')) {
            return true
          }
          return TREATMENT_EXHIBITION_RULES[treatment.disease.id].flowExclusion[
            'exclude_from_all_list_sanofi_flow'
          ].check(this.attendance)
        })
        // return this.treatments
      }

      if (this.attendance.disease.id === this.diseases.ESPONDILOARTRITIS && this.asasNotPositive) {
        return []
      }

      return this.treatments.filter((t) => {
        const exclusion = t.exhibitionConfig?.exclusion || []
        // const flowExclusion = t.exhibitionConfig?.flowExclusion || []
        const inclusion = t.exhibitionConfig?.inclusion || []
        return (
          !exclusion.some((configKey) =>
            TREATMENT_EXHIBITION_RULES[t.disease.id].exclusion[configKey].check(this.attendance),
          ) &&
          // !flowExclusion.some((configKey) =>
          //   TREATMENT_EXHIBITION_RULES[t.disease.id].flowExclusion[configKey].check(this.attendance),
          // ) &&
          inclusion.some((configKey) =>
            TREATMENT_EXHIBITION_RULES[t.disease.id].inclusion[configKey].check(this.attendance),
          )
        )
      })
    },
  },
  data() {
    return {
      dialog: {
        printingTemplate: {
          show: false,
          data: {},
          treatment: null,
          custom: false,
        },
      },
      selectedTemplate: null,
      phaseData: null,
      previousPhaseData: null,
      medicineOptions: [
        {
          text: 'Nenhum',
          value: 'none',
        },
        {
          text: 'Corticoide Tópico',
          value: 'corticoide_topico',
        },
        {
          text: 'Corticoide Injetável',
          value: 'corticoide_injetavel',
        },
        {
          text: 'Metotrexato',
          value: 'metotrexato',
        },
        {
          text: 'Ciclosporina',
          value: 'ciclosporina',
        },
        {
          text: 'Azatioprina',
          value: 'azatioprina',
        },
        {
          text: 'Micofenolato',
          value: 'micofenolato',
        },
        {
          text: 'Dupilumabe',
          value: 'dupilumabe',
        },
        {
          text: 'Fototerapia',
          value: 'fototerapia',
        },
        {
          text: 'Upadacitinibe',
          value: 'upadacitinibe',
        },
        {
          text: 'Baricitinibe',
          value: 'baricitinibe',
        },
        {
          text: 'Abrocitinibe',
          value: 'abrocitinibe',
        },
        {
          text: 'Outro',
          value: 'other',
        },
      ],
      diseases: DISEASES,
      buttons: [
        'bold',
        'italic',
        'underline',
        'strikethrough',
        'ul',
        'ol',
        'font',
        'fontsize',
        'paragraph',
        'lineHeight',
        'superscript',
        'subscript',
      ],
      guidelineDisplay: {},
      descriptionDisplay: {},
      dosageDisplay: {},
      showAllTreatments: false,
      loading: false,
      error: false,
      treatments: [],
      currentTreatment: null,
      examModal: false,
      templates: [],
      selectedMedicinePrescriptionTemplateId: null,
      selectedExamPrescriptionTemplateId: null,
    }
  },
  methods: {
    ...attendanceActionsMap(['startServerSyncing', 'endServerSyncing']),
    openDialog(dialog, data) {
      const dialogMap = {
        defaultPrescription: ({ treatment }) => {
          this.dialog.printingTemplate = {
            show: true,
            custom: false,
            treatment,
            data: {
              patientId: this.attendance.patient.id,
              text: `${treatment.medicine.name}: <br/> ${treatment.selectedDosage.value}`,
            },
          }
        },
        customPrescription: ({ customText }) => {
          this.dialog.printingTemplate = {
            show: true,
            custom: true,
            data: {
              patientId: this.attendance.patient.id,
              text: customText,
            },
          }
        },
      }

      dialogMap[dialog]?.(data)
    },
    ...patientActionsMap(['setPatient', 'patchPatient', 'setPatientCurrentWeightKg', 'setPatientCurrentHeightCm']),

    /**
     *
     * @param {{
     *  customText: string | undefined,
     *  treatment:{
     *    medicine:{
     *       id: number;
     *       name: string;
     *       description: string;
     *    }
     *    selectedDosage:{
     *      value:string
     *      [key: string]: unknown;
     *    }
     *  }
     * }} data
     */
    async saveAndPrintPrescription({ customText, treatment }) {
      try {
        await this.savePrescription()
        if (customText) {
          this.openDialog('customPrescription', { customText })
        } else {
          this.openDialog('defaultPrescription', { treatment })
        }
      } catch (error) {
        await this.endServerSyncing(false)
        if (showError) {
          this.$root.error(error)
        } else {
          this.$logger.warn('Não foi possível salvar os dados do atendimento no servidor!', error)
        }
      }
    },

    async savePrescription() {
      if (this.isServerSyncedData || this.isSavingData) {
        return
      }

      await this.startServerSyncing()
      await this.$api.patchAttendance(this.attendance.id, this.attendance)
      await this.endServerSyncing(true)
      return Promise.resolve({})
    },

    async closePrintTemplateDialog(selectedTemplate) {
      if (selectedTemplate) {
        await this.$api.changeOldPrescriptionTemplate({
          attendanceId: this.attendance.id,
          isCustomPrescription: this.dialog.printingTemplate.custom,
          templateId: selectedTemplate.id,
        })
      }

      this.dialog.printingTemplate = { show: false, data: {}, treatment: null, custom: false }
    },
    toggleGuideline(id) {
      this.$set(this.guidelineDisplay, id, !this.guidelineDisplay[id])
    },
    toggleDescription(id) {
      this.$set(this.descriptionDisplay, id, !this.descriptionDisplay[id])
    },
    toggleDosage(id) {
      this.$set(this.dosageDisplay, id, !this.dosageDisplay[id])
    },
    getRangeText(min, max, unit) {
      if (!min && !max) {
        return `sem restrições de ${unit === 'anos' ? 'idade' : 'peso'}`
      }
      if (!min) {
        return `até ${max} ${unit}`
      }
      if (!max) {
        return `a partir de ${min} ${unit}`
      }

      return `de ${min} a ${max} ${unit}`
    },
    printExamPrescriptionUrl(prescriptionId) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      } else {
        return `${this.$util.apiURL()}treatment/exam/print/${prescriptionId}/${this.attendance.id}/${
          this.selectedExamPrescriptionTemplateId
        }`
      }
    },

    getPrintMedicinePrescriptionUrl(treatment) {
      if (this.$util.apiURL()[this.$util.apiURL().length - 1] !== '/') {
        return `${this.$util.apiURL()}/treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.selectedMedicinePrescriptionTemplateId}`
      } else {
        return `${this.$util.apiURL()}treatment/dosage/print/${treatment.selectedDosage?.id}/${this.attendance.id}/${
          treatment.selectedDosage?.key === 'firstPrescription'
        }/${this.selectedMedicinePrescriptionTemplateId}`
      }
    },
    openPrintExamPrescriptionModal(treatment) {
      this.currentTreatment = treatment
      this.examModal = true
    },
    getTreatmentDosagesOptions(dosages) {
      let result = []
      dosages = dosages.filter((d) => {
        const patientAge = this.$util.toAgeNumber(this.attendancePatient.birthday)
        const patientWeight = this.attendancePatient.currentWeightKg

        return (
          (d.minAge || 0) <= patientAge &&
          (d.maxAge || Infinity) >= patientAge &&
          (d.minWeight || 0) <= patientWeight &&
          (d.maxWeight || Infinity) >= patientWeight
        )
      })
      result.push(
        ...dosages.map((d) => ({
          key: 'firstPrescription',
          text: `Primeira prescrição | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.firstPrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )
      result.push(
        ...dosages.map((d) => ({
          key: 'maintenancePrescription',
          text: `Manutenção | ${this.getRangeText(d.minAge, d.maxAge, 'anos')} | ${this.getRangeText(
            d.minWeight,
            d.maxWeight,
            'kg',
          )}`,
          value: d.maintenancePrescription,
          id: d.id,
          lme: d.treatmentDosageLme,
        })),
      )

      return result
    },
    getDefaultData() {
      return {
        selectedTreatments: [],
        customPrescriptions: [],
        medicines: {
          selected: [],
          other: null,
        },
        weightKg: null,
        heightCm: null,
      }
    },
    addCustomPrescription() {
      if (!this.phaseData.customPrescriptions) {
        this.$set(this.phaseData, 'customPrescriptions', [])
      }

      this.phaseData.customPrescriptions.push('')
    },
    removeCustomPrescription(index) {
      this.phaseData.customPrescriptions.splice(index, 1)
    },
    getCustomPrescriptionUrl(index) {
      return `${this.$util.apiURL()}/treatment/custom-prescription/print/${this.attendance.id}/${
        this.selectedMedicinePrescriptionTemplateId
      }/${index}`
    },
    async loadData() {
      try {
        this.loading = true
        this.treatments = (await this.$api.getTreatmentsForDisease(this.attendanceDiseaseId)).items
          .map((t) => {
            let result = { ...t }
            if (t.dosages[0]?.firstPrescription) {
              result.selectedDosage = {
                key: 'firstPrescription',
                value: t.dosages[0]?.firstPrescription,
                id: t.dosages[0]?.id,
                lme: t.dosages[0]?.treatmentDosageLme,
              }
            }
            return result
          })
          .filter((t) =>
            t.dosages.some((d) => {
              const patientAge = this.$util.toAgeNumber(this.attendancePatient.birthday)
              const patientWeight = this.attendancePatient.currentWeightKg
              return (
                (d.minAge || 0) <= patientAge &&
                (d.maxAge || Infinity) >= patientAge &&
                (d.minWeight || 0) <= patientWeight &&
                (d.maxWeight || Infinity) >= patientWeight
              )
            }),
          )

        this.templates = (await this.$api.getTemplates()).items

        if (this.medicinePrescriptionTemplates.length > 0) {
          this.selectedMedicinePrescriptionTemplateId = this.medicinePrescriptionTemplates[0].id
        }
        if (this.examPrescriptionTemplates.length > 0) {
          this.selectedExamPrescriptionTemplateId = this.examPrescriptionTemplates[0].id
        }

        this.phaseData.selectedTreatments = [
          ...this.phaseData.selectedTreatments.map((t) => ({
            ...this.treatments.find((t2) => t2.id === t.id),
            selectedDosage: t.selectedDosage,
          })),
        ]

        this.error = false
      } catch (error) {
        this.$root.error(error)
        this.error = true
      } finally {
        this.loading = false
      }
    },
    /* Salva os dados atuais antes do usuário editar um campo */
    savePreviousPhaseData() {
      this.previousPhaseData = { ...this.phaseData }
    },
    /* Se houve alteração em um campo, salva o dado no bd */
    async patchNewPhaseData() {
      for (const key in this.phaseData) {
        if (this.phaseData[key] === this.previousPhaseData[key]) continue
        switch (key) {
          case 'heightCm':
            this.setPatientCurrentHeightCm(this.phaseData[key])
            break
          case 'weightKg':
            this.setPatientCurrentWeightKg(this.phaseData[key])
            break
          default:
            break
        }
        this.attendance.heightCm = this.patientCurrentHeightCm
        this.attendance.weightKg = this.patientCurrentWeightKg
        await this.patchPatient()
        await this.$api.patchAttendance(this.attendance.id, this.attendance)
      }
    },
  },
  async mounted() {
    if (!!this.phaseData) {
      this.phaseData.weightKg = this.attendancePatient.currentWeightKg
      this.phaseData.heightCm = this.attendancePatient.currentHeightCm
    }
    // Vuex não persiste em refresh, então é necessário setar o paciente novamente
    this.setPatient(this.attendancePatient)
    await this.loadData()
  },
}
</script>

<style scoped>
.title-text {
  color: #2b2b2b;
}
</style>
